import { shortId } from '@/shared/lib/shortId'
import type { PanelUser } from './identityApi'

/**
 * AKTYOR IDENTIFIKATORINI ODAM NOMIGA AYLANTIRISH.
 *
 * =====================================================================
 * ⚠️ QAYSI NUQSONNI YOPADI — tashqi tester, B4 (2026-08-14)
 * =====================================================================
 * Ombor harakatlari jurnalida va ikkinchi imzo ekranida foydalanuvchi
 * o'rniga XOM UUID ko'rinardi:
 *
 *   Harakat jurnali    `StockMovementsPage`            `row.actorId`
 *   Tasdiq ro'yxati    `StockApprovalsPage`            `row.requestedBy`, `row.decidedBy`
 *   Qaror oynasi       `StockApprovalDecisionModal`    `approval.requestedBy`
 *
 * To'rttasi ham qiymatni SHUNDAYLIGICHA chizardi. Operator «kim
 * hisobdan chiqargan» degan savolga javob ololmasdi — UUID ni
 * qo'lda System › Users ekranidan qidirishi kerak edi.
 *
 * =====================================================================
 * ⚠️ NEGA PANELDA, BACKENDDA EMAS
 * =====================================================================
 * `docs/00-ARCHITECTURE.md` §4, 1- va 5-qoida: modul o'zga modulning
 * jadvaliga murojaat qilmaydi va kesishgan `JOIN` taqiqlangan
 * (`ArchUnit` build'ni yiqitadi). `inventory` uchun
 * `identity.app_user` — AYNAN o'zga sxema.
 *
 * Qolgan ikki yo'l ham yaramadi:
 *   · nomni yozuv paytida MUHRLASH — gateway faqat UUID uzatadi
 *     (`XsibHeaders.USER`), ism umuman kelmaydi;
 *   · `inventory` → `identity` HTTP klienti — yangi ichki endpoint,
 *     yangi servis tokeni va jurnalning HAR qatorida tarmoq
 *     chaqiruvi.
 *
 * Panel esa foydalanuvchilar ro'yxatini ALLAQACHON oladi
 * (`fetchUsers`), ya'ni bu yerda yangi tarmoq yuki YO'Q — bitta
 * keshlangan so'rov ikkala ekranga yetadi.
 *
 * =====================================================================
 * ⚠️ NEGA «TOPILMADI» HOLATI JIM QOLMAYDI
 * =====================================================================
 * Ro'yxat ikki sababdan to'liq bo'lmasligi mumkin va IKKALASI HAM
 * odatiy hol:
 *   · chaqiruvchida `user.read` yo'q — so'rov umuman yuborilmaydi;
 *   · aktyor o'chirilgan yoki u odam emas (fon vazifasi, migratsiya).
 *
 * Bunday holatda qiymat YO'QOTILMAYDI — u qisqartirilgan shaklda
 * ko'rsatiladi (`3ae69369…`), ya'ni operator uni baribir nusxa olib
 * qidira oladi. Bo'sh chiziqcha chizish ma'lumotni YASHIRARDI va bu
 * xom UUID dan ham yomon bo'lardi.
 */

/** Bitta chaqiruvda tayyorlanadigan `id → ko'rsatiladigan nom` xaritasi. */
export type ActorDirectory = ReadonlyMap<string, string>

/**
 * Xarita AKTYORNING IKKALA shakli bo'yicha ham kalitlanadi.
 *
 * ⚠️ Bu ataylab: `stock_approval.requested_by` ga server
 * `auth.getName()` yozadi va u `X-Xsib-Username` sarlavhasi
 * bo'lmaganda UUID, bo'lganda esa LOGIN bo'ladi
 * (`PermissionHeaderFilter`). Ya'ni bazadagi eski qatorlarda UUID,
 * yangilarida login turishi MUMKIN. Faqat bittasi bo'yicha
 * kalitlash jurnalning bir qismini jimgina qisqartirardi.
 */
export function buildActorDirectory(users: readonly PanelUser[]): ActorDirectory {
  const directory = new Map<string, string>()
  for (const user of users) {
    const label = actorDisplayName(user)
    directory.set(user.id, label)
    directory.set(user.username, label)
  }
  return directory
}

/**
 * Bitta foydalanuvchining ekranda ko'rinadigan nomi.
 *
 * ⚠️ Login QAVS ichida SAQLANADI, tashlanmaydi: ikki xodimning ismi
 * bir xil bo'lishi mumkin (`Aliyev A.`), login esa yagona. Faqat
 * ismni ko'rsatish «qaysi Aliyev» degan savolni tug'dirardi.
 *
 * ⚠️ `displayName` bo'sh bo'lsa login YOLG'IZ qoladi — «(login)»
 * shaklidagi qavsli bo'shliq emas. `identityApi.toPanelUser`
 * allaqachon `displayName ?? username` qiladi, ya'ni bu yerda
 * ikkalasi TENG bo'lishi odatiy hol.
 */
export function actorDisplayName(user: PanelUser): string {
  const name = user.displayName.trim()
  const login = user.username.trim()
  if (name === '' || name === login) return login
  return `${name} (${login})`
}

/**
 * Jurnal/tasdiq ekranlarida chiziladigan MATN.
 *
 * @param raw       serverdan kelgan `actorId` / `requestedBy` / `decidedBy`
 * @param directory {@link buildActorDirectory} natijasi
 * @param dash      bo'sh qiymat uchun belgi (`t('common.dash')`)
 *
 * ⚠️ Tartib muhim: avval xarita, keyin qisqartirish. Teskarisida
 * nom topilgan aktyor ham qisqargan UUID bo'lib ko'rinardi.
 *
 * ⚠️ Zaxira shakl SHU YERDA yozilmaydi — u `shared/lib/shortId` da,
 * B5 dagi qurilma bilan BIR XIL funksiya. Sabab o'sha faylda:
 * nusxa ko'chirilgan zaxira shakl bir aktyorni ikki ekranda ikki
 * xil ko'rsatardi.
 */
export function actorLabel(
  raw: string | null | undefined,
  directory: ActorDirectory,
  dash: string,
): string {
  if (raw === null || raw === undefined) return dash
  const value = raw.trim()
  if (value === '') return dash
  return directory.get(value) ?? shortId(value)
}

// ⚠️ Hook ATAYLAB boshqa faylda (`useActorDirectory.ts`): shu fayl
//    React'ga, `react-query` ga va `AuthContext` ga BOG'LANMASIN.
//    Sabab amaliy — `tests/actor-name.mjs` yuqoridagi sof funksiyalarni
//    `esbuild` bilan yig'ib, brauzersiz yurgizadi. Hook shu yerda
//    tursa, test butun autentifikatsiya daraxtini tortib kelardi va
//    o'lchayotgan narsasiga aloqasi bo'lmagan sabablardan yiqilardi.
